/* eslint-disable camelcase */
import { FC, useMemo } from 'react';

import {
  Avatar,
  Button,
  Heading,
  Loader,
  Text,
  Tooltip,
} from '@livechat/design-system-react-components';

import { Layout } from 'components/layout/Layout';
import { getAccessToken } from 'helpers/account';
import { useAccounts } from 'hooks/use-accounts';
import { useCanJoinToProduct } from 'hooks/use-can-join-to-product';
import { getProductDisplayName } from 'helpers/product';
import { pluralize } from 'helpers/string';
import { Account } from 'services/connectivity/accounts/types';

import { useJoinProduct } from '../product-buttons/use-join-product';
import { JoinProductBlocked } from './JoinProductBlocked';

import * as styles from './styles';

export const MAX_AVATARS_IN_GALLERY = 5;
export const MAX_ACCOUNTS_NO_FOR_COUNTER = 103;

type Props = {
  currentProduct: string;
  organizationId: string;
};

export const JoinProduct: FC<Props> = ({ currentProduct, organizationId }) => {
  const { joiningBlocked, isProcessing } = useCanJoinToProduct(currentProduct);
  const { accounts, isFetching } = useAccounts();
  const { handleJoin, isLoading } = useJoinProduct({
    organization_id: organizationId,
    product: currentProduct,
  });

  const accountsInCurrentProduct = accounts.filter((account) =>
    account.roles.some((role) => role.product === currentProduct)
  );

  const getCounterLabel = (numberOfAccounts: number): string => {
    if (numberOfAccounts > MAX_ACCOUNTS_NO_FOR_COUNTER) {
      return '99+';
    }
    return `+${accountsInCurrentProduct.length - MAX_AVATARS_IN_GALLERY + 1}`;
  };

  const organizationOwner: Account = useMemo(
    () =>
      accounts?.find((account) =>
        account.roles.find(
          (role) =>
            role.product === 'Accounts' &&
            role.role === 'owner' &&
            role.organization_id === organizationId
        )
      ),
    [accounts]
  );

  if (isFetching || isProcessing) {
    return (
      <Layout showHeaderLogo>
        <div css={styles.joinContainer}>
          <Loader size="large" />
        </div>
      </Layout>
    );
  }

  if (joiningBlocked) {
    return <JoinProductBlocked organizationId={organizationId} />;
  }

  return (
    <Layout showHeaderLogo showDropdown>
      <div css={styles.joinContainer}>
        {!!accountsInCurrentProduct.length && (
          <div
            css={styles.avatarsContainer(accountsInCurrentProduct.length)}
            data-testid="join-screen-avatar-gallery"
          >
            {accountsInCurrentProduct.map(({ properties, email, name }, index) => (
              <>
                {index < MAX_AVATARS_IN_GALLERY - 1 && (
                  <Avatar
                    size="xxlarge"
                    css={styles.avatar(accountsInCurrentProduct.length, index)}
                    type={properties.avatar_url ? 'image' : 'text'}
                    src={properties.avatar_url}
                    text={name || email}
                    data-testid="join-screen-avatar-gallery-item"
                  />
                )}
                {index === MAX_AVATARS_IN_GALLERY - 1 &&
                  accountsInCurrentProduct.length === MAX_AVATARS_IN_GALLERY && (
                    <Avatar
                      size="xxlarge"
                      css={styles.avatar(accountsInCurrentProduct.length, index)}
                      type={properties.avatar_url ? 'image' : 'text'}
                      src={properties.avatar_url}
                      text={name || email}
                      data-testid="join-screen-avatar-gallery-item"
                    />
                  )}
                {index >= MAX_AVATARS_IN_GALLERY &&
                  index === accountsInCurrentProduct.length - 1 && (
                    <Heading
                      size="lg"
                      css={styles.accountsCounter()}
                      data-testid="join-screen-avatar-gallery-item"
                    >
                      {getCounterLabel(accountsInCurrentProduct.length)}
                    </Heading>
                  )}
              </>
            ))}
          </div>
        )}
        <div css={styles.header}>
          <Heading size="md" bold>
            Join your team in&nbsp;
          </Heading>
          <Heading size="md">{getProductDisplayName(currentProduct, true)}</Heading>
        </div>
        {!!accountsInCurrentProduct.length && (
          <div css={styles.description}>
            <Text>
              {accountsInCurrentProduct.length}{' '}
              {pluralize(accountsInCurrentProduct.length, 'teammate')} already joined.
            </Text>
          </div>
        )}

        {currentProduct === 'PartnerProgram' ? (
          <Tooltip
            triggerRenderer={() => (
              <Button size="large" kind="primary" disabled>
                Join {getProductDisplayName(currentProduct, true)}
              </Button>
            )}
            kind="invert"
            placement="top"
          >
            <Text>
              Only the Owner <b>{organizationOwner?.email}</b> can create the Partner profile and
              access the Partner App.
            </Text>
          </Tooltip>
        ) : (
          <Button
            kind="primary"
            size="large"
            loading={isLoading}
            onClick={() => handleJoin(getAccessToken())}
          >
            Join {getProductDisplayName(currentProduct, true)}
          </Button>
        )}
      </div>
    </Layout>
  );
};
